import { useEffect, useMemo, useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

type ItemFormulario = {
  chave: number;
  produtoId: string;
  quantidade: string;
  saldoTotal: boolean;
};

let proximaChave = 1;

function novoItem(): ItemFormulario {
  return { chave: proximaChave++, produtoId: "", quantidade: "", saldoTotal: false };
}

function formatarNumero(valor: unknown, casas = 4) {
  return Number(valor ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: casas,
  });
}

function formatarMoeda(valor: unknown) {
  return Number(valor ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export default function BaixaDanoExtravio() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [emitenteId, setEmitenteId] = useState<number | null>(null);
  const [itens, setItens] = useState<ItemFormulario[]>([novoItem()]);

  useEffect(() => {
    if (!authLoading && !user) window.location.href = "/login";
  }, [authLoading, user]);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: user?.role === "admin",
  });
  const { data: produtos = [], isLoading: produtosCarregando } = trpc.products.list.useQuery(
    emitenteId ? { emitenteId } : undefined,
    { enabled: user?.role === "admin" && !!emitenteId }
  );

  const produtosComSaldo = useMemo(
    () => (produtos as any[]).filter((produto) => Number(produto.quantidade ?? 0) > 0),
    [produtos]
  );
  const produtosPorId = useMemo(
    () => new Map(produtosComSaldo.map((produto) => [Number(produto.id), produto])),
    [produtosComSaldo]
  );

  const mutation = trpc.baixaDanoExtravio.criar.useMutation({
    onSuccess: (resultado) => {
      toast.success(`Pedido #${resultado.pedidoId} criado. Resolve ${resultado.ticket}.`);
      setItens([novoItem()]);
    },
    onError: (erro) => toast.error(erro.message || "Não foi possível concluir a baixa."),
  });

  const atualizarItem = (chave: number, atualizacao: Partial<ItemFormulario>) => {
    setItens((atuais) => atuais.map((item) => item.chave === chave ? { ...item, ...atualizacao } : item));
  };

  const removerItem = (chave: number) => {
    setItens((atuais) => atuais.length === 1 ? atuais : atuais.filter((item) => item.chave !== chave));
  };

  const custoTotal = itens.reduce((total, item) => {
    const produto = produtosPorId.get(Number(item.produtoId));
    if (!produto) return total;
    const quantidade = item.saldoTotal ? Number(produto.quantidade ?? 0) : Number(item.quantidade || 0);
    return total + quantidade * Number(produto.custo ?? 0);
  }, 0);

  const confirmarBaixa = () => {
    if (!emitenteId) return toast.error("Selecione o emitente.");
    if (itens.some((item) => !item.produtoId)) return toast.error("Selecione o produto de todos os itens.");
    if (new Set(itens.map((item) => item.produtoId)).size !== itens.length) return toast.error("O mesmo produto foi selecionado mais de uma vez.");
    if (itens.some((item) => !item.saldoTotal && (!Number.isFinite(Number(item.quantidade)) || Number(item.quantidade) <= 0))) {
      return toast.error("Informe uma quantidade válida para todos os itens.");
    }
    const acimaDoSaldo = itens.find((item) => {
      const produto = produtosPorId.get(Number(item.produtoId));
      return produto && !item.saldoTotal && Number(item.quantidade) > Number(produto.quantidade ?? 0);
    });
    if (acimaDoSaldo) return toast.error("A quantidade informada não pode ultrapassar o saldo disponível.");
    if (!window.confirm(`Será criado um pedido finalizado com ${itens.length} item(ns), no total de ${formatarMoeda(custoTotal)}. Deseja continuar?`)) return;

    mutation.mutate({
      emitenteId,
      itens: itens.map((item) => ({
        produtoId: Number(item.produtoId),
        quantidade: item.saldoTotal ? undefined : Number(item.quantidade),
        saldoTotal: item.saldoTotal,
      })),
    });
  };

  if (authLoading) return <div className="p-6 text-sm text-muted-foreground">Carregando...</div>;
  if (!user || user.role !== "admin") {
    return <div className="p-6"><Card className="max-w-xl"><CardHeader><CardTitle>Acesso restrito</CardTitle><CardDescription>Este módulo é exclusivo para administradores.</CardDescription></CardHeader></Card></div>;
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Baixa por Dano e Extravio</h1>
        <p className="text-sm text-muted-foreground mt-1">Cria o pedido finalizado para o operador logístico, baixa o saldo e registra a ocorrência no Resolve.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Emitente</CardTitle>
          <CardDescription>Selecione o proprietário dos produtos que serão baixados.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-xl space-y-2">
            <Label htmlFor="emitente-baixa">Emitente</Label>
            <select
              id="emitente-baixa"
              value={emitenteId ?? ""}
              onChange={(event) => {
                setEmitenteId(event.target.value ? Number(event.target.value) : null);
                setItens([novoItem()]);
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Selecione o emitente</option>
              {(emitentes as any[]).map((emitente) => (
                <option key={emitente.id} value={emitente.id}>{emitente.razaoSocial || emitente.nomeFantasia || `Emitente ${emitente.id}`}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Itens</CardTitle>
          <CardDescription>Informe a quantidade de cada produto ou marque a baixa do saldo total.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!emitenteId ? (
            <div className="text-sm text-muted-foreground">Selecione o emitente para carregar os produtos.</div>
          ) : produtosCarregando ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando produtos...</div>
          ) : produtosComSaldo.length === 0 ? (
            <div className="text-sm text-muted-foreground">Nenhum produto com saldo disponível para este emitente.</div>
          ) : (
            <>
              {itens.map((item, index) => {
                const produto = produtosPorId.get(Number(item.produtoId));
                const selecionadosEmOutrasLinhas = new Set(itens.filter((outro) => outro.chave !== item.chave).map((outro) => outro.produtoId));
                return (
                  <div key={item.chave} className="rounded-md border p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium">Item {index + 1}</div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removerItem(item.chave)} disabled={itens.length === 1}>
                        <Trash2 className="h-4 w-4 mr-2" /> Remover
                      </Button>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-[minmax(280px,1fr)_140px_160px_180px] lg:items-end">
                      <div className="space-y-2">
                        <Label htmlFor={`produto-${item.chave}`}>Produto</Label>
                        <select
                          id={`produto-${item.chave}`}
                          value={item.produtoId}
                          onChange={(event) => atualizarItem(item.chave, { produtoId: event.target.value, quantidade: "", saldoTotal: false })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="">Selecione o produto</option>
                          {produtosComSaldo.filter((opcao) => !selecionadosEmOutrasLinhas.has(String(opcao.id))).map((opcao) => (
                            <option key={opcao.id} value={opcao.id}>{opcao.cod} — {opcao.descricao || "Sem descrição"}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Saldo</Label>
                        <Input value={produto ? formatarNumero(produto.quantidade) : ""} readOnly />
                      </div>
                      <div className="space-y-2">
                        <Label>Custo unitário</Label>
                        <Input value={produto ? formatarMoeda(produto.custo) : ""} readOnly />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`quantidade-${item.chave}`}>Quantidade</Label>
                        <Input
                          id={`quantidade-${item.chave}`}
                          type="number"
                          min="0.0001"
                          step="0.0001"
                          value={item.saldoTotal && produto ? String(produto.quantidade) : item.quantidade}
                          onChange={(event) => atualizarItem(item.chave, { quantidade: event.target.value })}
                          disabled={item.saldoTotal}
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer w-fit">
                      <input
                        type="checkbox"
                        checked={item.saldoTotal}
                        onChange={(event) => atualizarItem(item.chave, { saldoTotal: event.target.checked, quantidade: "" })}
                        disabled={!produto}
                        className="h-4 w-4"
                      />
                      Baixar saldo total deste produto
                    </label>
                  </div>
                );
              })}
              <Button type="button" variant="outline" onClick={() => setItens((atuais) => [...atuais, novoItem()])}>
                <Plus className="h-4 w-4 mr-2" /> Adicionar item
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Confirmação</CardTitle>
          <CardDescription>O pedido será criado como finalizado e a solicitação será aberta no Resolve com prazo padrão.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-4">
            <div className="text-sm text-muted-foreground">Custo total</div>
            <div className="text-2xl font-semibold mt-1">{formatarMoeda(custoTotal)}</div>
          </div>
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>A confirmação movimentará o saldo, criará o pedido finalizado e registrará a ocorrência no Resolve.</span>
          </div>
          <Button type="button" onClick={confirmarBaixa} disabled={mutation.isPending || !emitenteId || produtosComSaldo.length === 0}>
            {mutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processando...</> : "Confirmar baixa"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
